import GenericTable from "@/components/GenericTable";
import OtpApprovalModal from "@/components/OtpApprovalModal";
import { useOpenTotpSetup } from "@/components/TotpSetupModal";
import Footer from "@/components/Footer";
import { toaster } from "@/components/ui/toaster";
import { WITHDRAWALS_TABLE_COLUMNS } from "@/config/tables";
import TablesStateContext from "@/contexts/TablesStateContext";
import { useFetchUser } from "@/hooks/useFetchUser";
import { usePermissions } from "@/hooks/usePermissions";
import { Badge, Box, Button, HStack, Table, Text } from "@chakra-ui/react";
import { Check, X } from "lucide-react";
import { useContext, useState } from "react";
import { useApproveWithdrawal } from "../hooks/useApproveWithdrawal";
import { useCancelWithdrawal } from "../hooks/useCancelWithdrawal";
import { useFetchWithdrawals } from "../hooks/useFetchWithdrawals";

const TABLE_KEY = "payouts-withdrawals";

export default function WithdrawalsTable() {
  const { getTableState, setCursor } = useContext(TablesStateContext);
  const tableState = getTableState(TABLE_KEY);
  const cursor = tableState.cursor;

  const [otpModalWithdrawalId, setOtpModalWithdrawalId] = useState<
    string | null
  >(null);
  const [otpError, setOtpError] = useState<string | null>(null);

  const { user } = useFetchUser();
  const openTotpSetup = useOpenTotpSetup();

  const { withdrawals, nextCursor, isLoading } = useFetchWithdrawals(
    {},
    cursor
  );
  const { isPayoutApprover } = usePermissions();
  const { approveWithdrawal, isApproving } = useApproveWithdrawal(
    () => {
      setOtpModalWithdrawalId(null);
      setOtpError(null);
      toaster.create({
        title: "Retiro aprobado",
        description: "El retiro ha sido aprobado y se procesará en breve.",
        type: "success",
        duration: 5000,
      });
    },
    (error) => {
      console.error("Error approving withdrawal:", error);
      const message = error instanceof Error ? error.message : "";
      if (otpModalWithdrawalId && message.includes("TOTP locked")) {
        setOtpError(
          "Clave Dinámica bloqueada temporalmente. Intenta más tarde."
        );
        return;
      }
      if (otpModalWithdrawalId && message.includes("Invalid OTP")) {
        setOtpError("Clave Dinámica inválida. Intenta de nuevo.");
        return;
      }
      if (message.includes("Insufficient balance")) {
        toaster.create({
          title: "Saldo insuficiente",
          description: "No tienes saldo disponible para aprobar este retiro.",
          type: "error",
          duration: 5000,
        });
      } else {
        toaster.create({
          title: "Error",
          description:
            "No se pudo aprobar el retiro. Por favor intenta de nuevo.",
          type: "error",
          duration: 5000,
        });
      }
    }
  );
  const { cancelWithdrawal, isCancelling } = useCancelWithdrawal(
    () => {
      toaster.create({
        title: "Retiro cancelado",
        description: "El retiro ha sido cancelado.",
        type: "success",
        duration: 5000,
      });
    },
    () => {
      toaster.create({
        title: "Error",
        description:
          "No se pudo cancelar el retiro. Por favor intenta de nuevo.",
        type: "error",
        duration: 5000,
      });
    }
  );

  const columns = [
    { key: "createdAt", label: "Fecha", width: "15%" },
    { key: "amount", label: "Monto", width: "15%" },
    { key: "totalFee", label: "Comisión", width: "15%" },
    { key: "discountedAmount", label: "Monto Descontado", width: "15%" },
    { key: "status", label: "Estado", width: "10%" },
    { key: "account", label: "Cuenta Destino", width: "30%" },
    { key: "actions", label: "", width: "15%" },
  ];

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat("es-CO", {
      style: "currency",
      currency: "COP",
      maximumFractionDigits: 0,
    }).format(amount);
  };

  const formatDate = (dateString: string) => {
    return new Intl.DateTimeFormat("es-CO", {
      dateStyle: "medium",
      timeStyle: "short",
    }).format(new Date(dateString));
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case "pending":
        return (
          <Badge colorPalette="orange" variant="subtle">
            Pendiente
          </Badge>
        );
      case "completed":
        return (
          <Badge colorPalette="green" variant="subtle">
            Completado
          </Badge>
        );
      case "processing":
        return (
          <Badge colorPalette="blue" variant="subtle">
            En proceso
          </Badge>
        );
      case "cancelled":
        return (
          <Badge colorPalette="red" variant="subtle">
            Cancelado
          </Badge>
        );
      case "declined":
        return (
          <Badge colorPalette="red" variant="subtle">
            Error
          </Badge>
        );
      default:
        return (
          <Badge colorPalette="gray" variant="subtle">
            {status}
          </Badge>
        );
    }
  };

  return (
    <Box display="flex" flexDirection="column" flex="1" minH={0}>
      <GenericTable isLoading={isLoading} columns={columns} fillHeight>
        {withdrawals.map((withdrawal) => (
          <Table.Row key={withdrawal.id}>
            <Table.Cell color="gray.600">
              {formatDate(withdrawal.createdAt)}
            </Table.Cell>
            <Table.Cell>
              <Text fontWeight="bold" color="gray.800">
                {formatCurrency(withdrawal.amount)}
              </Text>
            </Table.Cell>
            <Table.Cell>
              <Text fontWeight="medium" color="gray.600">
                {formatCurrency(withdrawal.totalFee)}
              </Text>
            </Table.Cell>
            <Table.Cell>
              <Text fontWeight="bold" color="gray.800">
                {formatCurrency(withdrawal.amount + withdrawal.totalFee)}
              </Text>
            </Table.Cell>
            <Table.Cell>{getStatusBadge(withdrawal.status)}</Table.Cell>
            <Table.Cell color="gray.600">{withdrawal.account}</Table.Cell>
            <Table.Cell>
              {isPayoutApprover && withdrawal.status === "pending" && (
                <HStack gap={2} justify="flex-end">
                  <Button
                    size="sm"
                    colorPalette="red"
                    variant="ghost"
                    onClick={() => cancelWithdrawal(withdrawal.id)}
                    disabled={isCancelling}
                  >
                    <X size={16} />
                  </Button>
                  <Button
                    size="sm"
                    colorPalette="green"
                    variant="solid"
                    onClick={() => {
                      if (user?.showTotpSecretConfiguration) {
                        openTotpSetup();
                        return;
                      }
                      setOtpModalWithdrawalId(withdrawal.id);
                      setOtpError(null);
                    }}
                    disabled={isApproving}
                  >
                    <Check size={16} />
                  </Button>
                </HStack>
              )}
            </Table.Cell>
          </Table.Row>
        ))}
      </GenericTable>
      <Box mt="auto" pt={6} flexShrink={0}>
        <Footer
          tableProps={{
            fileName: "retiros",
            items: withdrawals,
            columns: WITHDRAWALS_TABLE_COLUMNS,
            pagination: {
              tableKey: TABLE_KEY,
              nextCursor,
              setCursor: (c: string | undefined) => setCursor(TABLE_KEY, c),
              cursor,
            },
          }}
        />
      </Box>

      <OtpApprovalModal
        isOpen={!!otpModalWithdrawalId}
        onClose={() => {
          setOtpModalWithdrawalId(null);
          setOtpError(null);
        }}
        onConfirm={(otp) => {
          if (otpModalWithdrawalId) {
            setOtpError(null);
            approveWithdrawal(otpModalWithdrawalId, otp);
          }
        }}
        hasTotpSecret={user?.hasTotpSecret ?? false}
        isLoading={isApproving}
        error={otpError}
      />
    </Box>
  );
}
